import { z } from 'zod';
import {
  ALERT_MUTE_CHOICES,
  ALERT_MUTE_REASON_MAX,
  ALERT_RESOLUTION_NOTE_MAX,
  ALERT_SNOOZE_CHOICES,
} from '../alerts';
import { isoDate } from './date';
import { idempotency } from './spine';
import { ticketCreateInput } from './ticket';

/**
 * The alerts board and its writes (ops insights Phase 7 spec §6). Every write names its alert and
 * carries the idempotency envelope: a retry lands once, and replays the first answer.
 */
const onAlert = idempotency.extend({ alertId: z.string().uuid() });

/**
 * `all`: what is waiting (not snoozed); `mine`: what the caller owns (not snoozed); `snoozed`:
 * only the snoozed ones.
 */
export const ALERT_BOARD_VIEWS = ['all', 'mine', 'snoozed'] as const;
export type AlertBoardView = (typeof ALERT_BOARD_VIEWS)[number];

export const alertBoardInput = z.object({
  view: z.enum(ALERT_BOARD_VIEWS).default('all'),
});
export type AlertBoardInput = z.infer<typeof alertBoardInput>;

export const alertIdInput = z.object({ alertId: z.string().uuid() });
export type AlertIdInput = z.infer<typeof alertIdInput>;

/** Take it (the caller), or hand it back to the office queue. */
export const alertTakeInput = onAlert;
export type AlertTakeInput = z.infer<typeof alertTakeInput>;
export const alertUnassignInput = onAlert;
export type AlertUnassignInput = z.infer<typeof alertUnassignInput>;

/** Name who is on it: an owner or office member of the shop. */
export const alertAssignInput = onAlert.extend({ ownerId: z.string().uuid() });
export type AlertAssignInput = z.infer<typeof alertAssignInput>;

/** A date exactly when the choice is `date`. */
const datedChoice = (v: { choice: string; date?: string }) =>
  (v.choice === 'date') === (v.date !== undefined);

export const alertSnoozeInput = onAlert
  .extend({ choice: z.enum(ALERT_SNOOZE_CHOICES), date: isoDate.optional() })
  .refine(datedChoice, { message: 'A date goes with "until a date", and only with it' });
export type AlertSnoozeInput = z.infer<typeof alertSnoozeInput>;

export const alertUnsnoozeInput = onAlert;
export type AlertUnsnoozeInput = z.infer<typeof alertUnsnoozeInput>;

/** "Resolve…": what was done, required (spec §3). */
export const alertResolveInput = onAlert.extend({
  note: z.string().trim().min(1, 'Say what was done').max(ALERT_RESOLUTION_NOTE_MAX),
});
export type AlertResolveInput = z.infer<typeof alertResolveInput>;

/** "Mute…" the alert's subject: why, everyone sees it, and until when. */
export const alertMuteInput = onAlert
  .extend({
    reason: z.string().trim().min(1, 'Say why').max(ALERT_MUTE_REASON_MAX),
    choice: z.enum(ALERT_MUTE_CHOICES),
    date: isoDate.optional(),
  })
  .refine(datedChoice, { message: 'A date goes with "until a date", and only with it' });
export type AlertMuteInput = z.infer<typeof alertMuteInput>;

/**
 * Useful / Not useful on an alert in the testing review (spec §7, verb `review`): one judgment
 * per person per alert, the latest one standing.
 */
export const alertJudgeInput = onAlert.extend({ useful: z.boolean() });
export type AlertJudgeInput = z.infer<typeof alertJudgeInput>;

/** Lift a mute, by the mute itself (Settings lists mutes, not alerts). */
export const alertUnmuteInput = idempotency.extend({ muteId: z.string().uuid() });
export type AlertUnmuteInput = z.infer<typeof alertUnmuteInput>;

/**
 * "Open a ticket" from an alert whose subject has a customer (spec §4): the ticket's title,
 * category and priority, with the same rules as "New ticket". The customer, and the job when
 * the subject is one, are the server's to read off the alert's subject, never the form's.
 */
export const alertOpenTicketInput = onAlert.extend(
  ticketCreateInput.pick({ title: true, category: true, priority: true }).shape,
);
export type AlertOpenTicketInput = z.infer<typeof alertOpenTicketInput>;

/**
 * The caller's own "Morning alerts email" switch (spec §8). No user id: a person changes only
 * their own choice (the service reads the caller from the session).
 */
export const alertDigestPrefInput = z.object({ digest: z.boolean() }).strict();
export type AlertDigestPrefInput = z.infer<typeof alertDigestPrefInput>;
